import { User, LoginCredentials } from '../types';
import { getDB, getAuthInstance } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { signInWithEmailAndPassword } from 'firebase/auth';

// Mock users data - in a real app, this would come from an API
const mockUsers: User[] = [
  {
    id: '1',
    username: 'admin',
    role: 'admin',
    email: 'admin@college.edu',
    firstName: 'Admin',
    lastName: 'User',
    isActive: true,
    createdAt: new Date('2023-01-01'),
    lastLogin: new Date(),
  },
  {
    id: '2',
    username: 'faculty1',
    role: 'faculty',
    email: 'faculty1@college.edu',
    firstName: 'John',
    lastName: 'Smith',
    isActive: true,
    createdAt: new Date('2023-01-15'),
    lastLogin: new Date(),
  },
  {
    id: '3',
    username: 'staff1',
    role: 'staff',
    email: 'staff1@college.edu',
    firstName: 'Jane',
    lastName: 'Doe',
    isActive: true,
    createdAt: new Date('2023-02-01'),
    lastLogin: new Date(),
  },
];

// Mock passwords - in a real app, passwords would be hashed and stored securely
const mockPasswords: Record<string, string> = {
  admin: 'admin123',
  faculty1: 'faculty123',
  staff1: 'staff123',
};

// Helper function to convert various date formats to Date object
function parseDate(dateValue: any): Date {
  if (!dateValue) return new Date();
  
  // If it's a Firestore Timestamp
  if (dateValue && typeof dateValue.toDate === 'function') {
    return dateValue.toDate();
  }
  
  // If it's already a Date object
  if (dateValue instanceof Date) {
    return dateValue;
  }
  
  // If it's a string or number, try to parse it
  const parsed = new Date(dateValue);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

class AuthService {
  private currentUser: User | null = null;
  private token: string | null = null;

  async login(credentials: LoginCredentials): Promise<User> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    const { username, password } = credentials;
    
    console.log('Login attempt:', { username, password: '***' });
    
    try {
      // First, try to find the user in Firebase
      const usersRef = collection(getDB(), 'users');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);
      
      console.log('Firebase query results:', querySnapshot.empty ? 'No users found' : `${querySnapshot.size} user(s) found`);
      
      if (!querySnapshot.empty) {
        // Found user in Firebase
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        
        console.log('Found user data:', { 
          username: userData.username, 
          hasPassword: !!userData.password,
          hasEmail: !!userData.email,
          isActive: userData.isActive,
          role: userData.role,
          fullUserData: userData 
        });
        
        // Check if user is active (bypass for admin role)
        if (!userData.isActive && userData.role !== 'admin') {
          throw new Error('Account is disabled. Please contact administrator.');
        }
        
        // Check if user has password stored in Firestore
        if (userData.password) {
          // User has password in Firestore, validate directly
          console.log('Checking password against Firestore stored password');
          if (userData.password !== password) {
            throw new Error('Invalid username or password');
          }
          
          // Password is correct, create user object
          const user: User = {
            id: userDoc.id,
            username: userData.username,
            role: userData.role,
            email: userData.email || '',
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            isActive: userData.isActive ?? true, // Default to true if missing
            createdAt: parseDate(userData.createdAt),
            lastLogin: new Date()
          };
          
          // Generate token and store user
          this.token = `firebase-token-${userDoc.id}-${Date.now()}`;
          this.currentUser = user;
          
          // Store in localStorage
          localStorage.setItem('authToken', this.token);
          localStorage.setItem('currentUser', JSON.stringify(user));
          
          console.log('Login successful for Firebase user');
          return user;
        } else if (userData.email) {
          // Legacy user without password in Firestore, try Firebase Auth
          console.log('Attempting Firebase Auth login');
          try {
            // Try to authenticate with Firebase auth
            const userCredential = await signInWithEmailAndPassword(
              getAuthInstance(),
              userData.email,
              password
            );
            
            // Authentication successful, update and return user
            const user: User = {
              id: userDoc.id,
              username: userData.username,
              role: userData.role,
              email: userData.email,
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              isActive: userData.isActive ?? true,
              createdAt: parseDate(userData.createdAt),
              lastLogin: new Date()
            };
            
            // Generate token and store user
            this.token = userCredential.user.uid;
            this.currentUser = user;
            
            // Store in localStorage
            localStorage.setItem('authToken', this.token);
            localStorage.setItem('currentUser', JSON.stringify(user));
            
            console.log('Login successful via Firebase Auth');
            return user;
          } catch (error) {
            console.error("Firebase authentication error:", error);
            throw new Error('Invalid username or password');
          }
        } else {
          throw new Error('This account needs to be migrated. Please contact an administrator.');
        }
      } else {
        // If not found in Firebase, try mock users as fallback
        console.log('User not found in Firebase, trying mock users');
        const mockUser = mockUsers.find(u => u.username === username);
        if (!mockUser) {
          throw new Error('Invalid username or password');
        }
        
        // Check if user is active
        if (!mockUser.isActive) {
          throw new Error('Account is disabled. Please contact administrator.');
        }
        
        // Check password
        if (mockPasswords[username] !== password) {
          throw new Error('Invalid username or password');
        }
        
        // Update last login
        mockUser.lastLogin = new Date();
        
        // Generate mock token
        this.token = `mock-token-${Date.now()}`;
        this.currentUser = mockUser;
        
        // Store in localStorage
        localStorage.setItem('authToken', this.token);
        localStorage.setItem('currentUser', JSON.stringify(mockUser));
        
        console.log('Login successful for mock user');
        return mockUser;
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }
  }

  logout(): void {
    this.currentUser = null;
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
  }

  async getCurrentUser(): Promise<User | null> {
    // Check if user is already loaded
    if (this.currentUser) {
      return this.currentUser;
    }

    // Try to get from localStorage
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('currentUser');

    if (!token || !userStr) {
      return null;
    }

    try {
      const user = JSON.parse(userStr) as User;
      // Convert date strings back to Date objects
      user.createdAt = new Date(user.createdAt);
      if (user.lastLogin) {
        user.lastLogin = new Date(user.lastLogin);
      }

      this.token = token;
      this.currentUser = user;
      return user;
    } catch (error) {
      // Clear invalid data
      this.logout();
      return null;
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Check old password
    if (mockPasswords[this.currentUser.username] !== oldPassword) {
      throw new Error('Current password is incorrect');
    }

    // Update password
    mockPasswords[this.currentUser.username] = newPassword;
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Update user data
    const updatedUser = {
      ...this.currentUser,
      ...updates,
      id: this.currentUser.id, // Prevent ID from being changed
      username: this.currentUser.username, // Prevent username from being changed
    };

    // Update in mock data
    const userIndex = mockUsers.findIndex(u => u.id === this.currentUser!.id);
    if (userIndex !== -1) {
      mockUsers[userIndex] = updatedUser;
    }

    this.currentUser = updatedUser;
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));

    return updatedUser;
  }

  getToken(): string | null {
    return this.token || localStorage.getItem('authToken');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  hasRole(role: string): boolean {
    return this.currentUser?.role === role;
  }

  hasPermission(permission: string): boolean {
    if (!this.currentUser) return false;

    // Define role-based permissions
    const permissions: Record<string, string[]> = {
      admin: [
        'users.create',
        'users.read',
        'users.update',
        'users.delete',
        'classes.create',
        'classes.read',
        'classes.update',
        'classes.delete',
        'attendance.create',
        'attendance.read',
        'attendance.update',
        'attendance.delete',
        'reports.create',
        'reports.read',
        'reports.export',
        'settings.read',
        'settings.update',
        'analytics.read',
      ],
      faculty: [
        'classes.read',
        'attendance.create',
        'attendance.read',
        'attendance.update',
        'reports.read',
        'reports.export',
        'analytics.read',
      ],
      staff: [
        'classes.read',
        'attendance.read',
        'reports.read',
        'analytics.read',
      ],
    };

    const rolePermissions = permissions[this.currentUser.role] || [];
    return rolePermissions.includes(permission);
  }

  // Mock method to get all users (admin only)
  async getAllUsers(): Promise<User[]> {
    if (!this.hasPermission('users.read')) {
      throw new Error('Access denied');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    return [...mockUsers];
  }

  // Mock method to create user (admin only)
  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'lastLogin'>): Promise<User> {
    if (!this.hasPermission('users.create')) {
      throw new Error('Access denied');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Check if username already exists
    if (mockUsers.some(u => u.username === userData.username)) {
      throw new Error('Username already exists');
    }

    const newUser: User = {
      ...userData,
      id: `user-${Date.now()}`,
      createdAt: new Date(),
    };

    mockUsers.push(newUser);
    return newUser;
  }

  // Mock method to update user (admin only)
  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    if (!this.hasPermission('users.update')) {
      throw new Error('Access denied');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const userIndex = mockUsers.findIndex(u => u.id === userId);
    if (userIndex === -1) {
      throw new Error('User not found');
    }

    const updatedUser = {
      ...mockUsers[userIndex],
      ...updates,
      id: userId, // Prevent ID from being changed
    };

    mockUsers[userIndex] = updatedUser;
    return updatedUser;
  }

  // Mock method to delete user (admin only)
  async deleteUser(userId: string): Promise<void> {
    if (!this.hasPermission('users.delete')) {
      throw new Error('Access denied');
    }

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const userIndex = mockUsers.findIndex(u => u.id === userId);
    if (userIndex === -1) {
      throw new Error('User not found');
    }

    // Don't allow deleting the current user
    if (userId === this.currentUser?.id) {
      throw new Error('Cannot delete your own account');
    }

    mockUsers.splice(userIndex, 1);
  }
}

export const authService = new AuthService(); 